import { useCallback } from "react";
import { useWorkspaceCopy } from "../../i18n/workspaceCatalog";
import { formatMessage, type MessageValues } from "../../i18n/catalogUtils";
export const workbenchEn = {
  incident_linkage: "Incident linkage",
  repeated_infrastructure: "Repeated infrastructure",
  recurring_artifact: "Recurring artifact",
  abuse_pattern: "Abuse pattern",
  file_sha256: "Exact file hash",
  indicator_digest: "Exact indicator digest",
  behavior_signature_digest: "Exact behavior signature",
  moreRecords: "Show {count} more records",
  scopeLimit:
    "This scope lists up to {count} recent retained entries. Unlisted data has not been compared.",
  sameCase: "Select entries from at least two authorized cases.",
  differentKind: "Select entries of the same signal kind.",
  selectGrant: "Select a Senior-authorized scope",
  noGrants:
    "No active comparison grant is available for this case. Ask your Senior reviewer to authorize a bounded comparison.",
  baseline: "Baseline capture",
  currentCapture: "Comparison capture",
  metadataOnly:
    "This comparison shows recorded capture metadata only. It does not claim behavioral recurrence, verified absence or comparable parser coverage.",
  findings: "Human findings",
  clearSearch: "Clear search",
  unknown: "Unknown / unresolved",
  overview: "Case overview",
  investigation: "Investigation",
  comparisons: "Comparisons",
  assessments: "Leads & hypotheses",
  report: "Report",
  graph: "Behavior graph",
  timeline: "Timeline",
  processes: "Processes",
  network: "Network",
  files: "Files",
  evidence: "Evidence & coverage",
  graphTitle: "Follow the behavior. Inspect every link.",
  graphBody:
    "Explore recorded relationships and their evidence. Time ordering alone does not establish causality.",
  search: "Search events, entities and source values",
  all: "All object types",
  observed: "Observed relationship",
  inferred: "Derived, inferred or contextual relationship",
  fit: "Fit graph",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  expand: "Expand canvas",
  restore: "Restore canvas",
  systemRunRecorded: "An automatic parser run is recorded.",
  systemRunAbsent: "No automatic parser run is recorded for this preparation job.",
  parserProfileMissing: "No active parser profile matched this evidence and Collector build. A compatible trusted parser must be configured.",
  parserBuildMissing: "A usable Collector build binding was not available for parser selection.",
  preparationTitle: "Evidence preparation",
  preparationCounts: "Admitted evidence: {evidence} · Preparation jobs: {jobs} · Missing preparation: {missing}",
  preparationBody: "Assignment readiness can include partial preparation. Inspect the parser result before treating an empty tool as a completed analysis.",
  parserStage: "Parser", correlationStage: "Correlation", enrichmentStage: "Enrichment", preparationError: "Preparation error code",
  stage_pending: "Pending", stage_queued: "Queued", stage_running: "Running", stage_succeeded: "Succeeded", stage_partial: "Partial", stage_unavailable: "Unavailable", stage_skipped: "Skipped", stage_failed: "Failed", stage_unknown: "Unknown",
  sourceCounts: "Normalized sources: {total} · Observations and events: {records} · Entities: {entities} · Complete relationships: {relationships}",
  noNormalizedSources: "The server returned no normalized sources for this case. Inspect parser runs and evidence coverage; an analysis snapshot alone does not confirm usable analysis data.",
  noModeSources: "No records of this type are present in the supplied sources. Inspect evidence and parser runs for coverage and preparation limitations.",
  noMatchingSources: "No supplied records match these filters. Clear the search or object-type filter to see other records.",
  inspectPreparation: "Inspect parser runs and evidence coverage",
  objects: "Objects",
  relationships: "Relationships",
  source: "Source inspector",
  close: "Close source inspector",
  noData: "No matching analytical records",
  noDataBody:
    "No records are available in this authorized projection. This does not establish that no suspicious behavior occurred.",
  unresolved:
    "{count} relationships have unavailable endpoints and are omitted from the graph.",
  graphEmpty:
    "No complete entity relationships are available. Explore the timeline or evidence instead.",
  related: "Linked records",
  time: "Observed time",
  unknownTime: "Time unavailable",
  state: "Evidence state",
  reference: "Exact object reference",
  provenance: "Source bindings",
  snapshot: "Analysis snapshot",
  details: "Recorded attributes",
  noProvenance: "No source binding is available.",
  useHypothesis: "Use in hypothesis",
  sources: "Selected sources",
  clearSources: "Clear selected sources",
  detections: "Automated leads",
  intel: "Threat intelligence",
  hypotheses: "Hypotheses",
  ai: "AI assistance",
  future: "Future capability",
  aiBody:
    "AI assistance is not enabled in this workbench. Future assistance must stay case-bound, cite selected sources and require human review; it cannot create an approved finding or release a report.",
  history: "Subject history",
  cross: "Authorized cases",
  historyBody:
    "Historical conclusions require verified subject identity, comparable capture windows, collection scope and parser versions. The current projection does not supply a validated historical comparison; no recurrence or absence is inferred.",
  snapshots: "Recorded acquisition snapshots",
  comparisonBoundary:
    "An exact match is not a compromise probability or campaign attribution. Raw evidence access remains case-bound.",
  grant: "Senior access grant",
  entries: "Explicit derived entry identifiers",
  compare: "Compare selected signals",
  notCompared: "No comparison has been run.",
  match: "Exact derived signal match",
  noMatch: "Selected signals did not match",
  comparisonError:
    "Comparison unavailable or authorization changed. Previous results have been cleared.",
  hypothesisStatement: "Hypothesis statement",
  rationale: "Confidence rationale",
  confidence: "Analyst confidence",
  save: "Save hypothesis",
  saved: "Hypothesis saved",
  pending: "Saving…",
  failed:
    "The operation could not be completed. Refresh and check your access before retrying.",
  loading: "Loading…",
  refresh: "Refresh",
  reason: "Evidence rationale",
  stance: "Evidence stance",
  supports: "Supports",
  contradicts: "Contradicts",
  limits: "Limits",
  attach: "Attach evidence",
  selectHypothesis: "Select a hypothesis",
  none: "No hypotheses are available for this case.",
  intelBody:
    "Recorded intelligence and enrichment snapshots are context, not a human verdict. A missing match does not establish safety. Inspect publication, retrieval, validity and source lineage before using a match.",
  rule: "Rule and version",
  uncertainty: "Time certainty",
  currentCase: "Current case",
  graphLimit:
    "Showing {shown} of {total} graph objects. Filter to narrow the canvas; the record list remains searchable.",
  selectRecord:
    "Select an object or relationship to inspect its recorded sources.",
  page: "Case investigation workbench",
  role: "Analyst",
  profile: "Profile & account",
  security: "Security & sessions",
  signout: "Sign out",
  help: "Help",
  helpBody:
    "Open an assigned case, inspect sources, record hypotheses and submit a report for Senior review. Automated leads do not constitute a finding.",
  notifications: "Notifications",
  account: "Account menu",
  markRead: "Mark read",
  markAll: "Mark all read",
  noNotifications: "No notifications",
  searchCases: "Search assigned cases",
  noResults: "No matching assigned cases",
  openSource: "Inspect source",
  sourceUnavailable: "Source is not present in this authorized projection.",
  created: "Recorded at",
  ends: "Window ends",
  precision: "Time precision",
  version: "Version",
  purpose: "Purpose",
  expires: "Expires",
  notFound: "Unavailable",
  attachSaved: "Evidence attached",
  latestOnly:
    "The latest active hypotheses are shown. Earlier revisions remain in the audit history.",
};
export type WorkbenchKey = keyof typeof workbenchEn;
export const workbenchFa: Record<WorkbenchKey, string> = {
  incident_linkage: "ارتباط رخدادها",
  repeated_infrastructure: "زیرساخت تکرارشونده",
  recurring_artifact: "آرتیفکت تکرارشونده",
  abuse_pattern: "الگوی سوءاستفاده",
  file_sha256: "هش دقیق فایل",
  indicator_digest: "چکیدهٔ دقیق شاخص",
  behavior_signature_digest: "امضای دقیق رفتار",
  moreRecords: "نمایش {count} دادهٔ بیشتر",
  scopeLimit:
    "این محدوده حداکثر {count} دادهٔ اخیرِ نگه‌داری‌شده را نشان می‌دهد. داده‌های خارج از فهرست مقایسه نشده‌اند.",
  sameCase: "داده‌هایی از دست‌کم دو کیس مجاز انتخاب کنید.",
  differentKind: "داده‌هایی از یک نوع سیگنال انتخاب کنید.",
  selectGrant: "انتخاب محدودهٔ مجاز تحلیلگر ارشد",
  noGrants:
    "مجوز مقایسهٔ فعالی برای این کیس موجود نیست. از بازبین ارشد خود بخواهید یک مقایسهٔ محدود را مجاز کند.",
  baseline: "جمع‌آوری مبنا",
  currentCapture: "جمع‌آوری مقایسه",
  metadataOnly:
    "این مقایسه فقط فرادادهٔ ثبت‌شدهٔ جمع‌آوری را نشان می‌دهد. تکرار رفتار، نبود تأییدشده یا پوشش یکسان پارسرها را اثبات نمی‌کند.",
  findings: "یافته‌های انسانی",
  clearSearch: "پاک کردن جست‌وجو",
  unknown: "نامشخص / حل‌نشده",
  overview: "نمای کلی کیس",
  investigation: "بررسی",
  comparisons: "مقایسه‌ها",
  assessments: "سرنخ‌ها و فرضیه‌ها",
  report: "گزارش",
  graph: "گراف رفتار",
  timeline: "خط زمانی",
  processes: "فرایندها",
  network: "شبکه",
  files: "فایل‌ها",
  evidence: "شواهد و پوشش",
  graphTitle: "رفتار را دنبال کنید. هر رابطه را بررسی کنید.",
  graphBody:
    "روابط ثبت‌شده و شواهد آن‌ها را بررسی کنید. ترتیب زمانی به‌تنهایی رابطهٔ علت و معلولی را اثبات نمی‌کند.",
  search: "جست‌وجوی رویداد، موجودیت و مقادیر منبع",
  all: "همهٔ انواع داده",
  observed: "رابطهٔ مشاهده‌شده",
  inferred: "رابطهٔ مشتق‌شده، استنباطی یا زمینه‌ای",
  fit: "نمایش کامل گراف",
  zoomIn: "بزرگ‌نمایی",
  zoomOut: "کوچک‌نمایی",
  expand: "گسترش بوم",
  restore: "بازگردانی بوم",
  systemRunRecorded: "اجرای خودکار پارسر ثبت شده است.",
  systemRunAbsent: "برای این کار آماده‌سازی، اجرای خودکار پارسر ثبت نشده است.",
  parserProfileMissing: "هیچ پروفایل پارسر فعالی با این شاهد و بیلد کالکتور تطابق نداشت. باید پارسر مورد اعتماد و سازگار پیکربندی شود.",
  parserBuildMissing: "اطلاعات اتصال به بیلد کالکتور برای انتخاب پارسر در دسترس نبوده است.",
  preparationTitle: "آماده‌سازی شواهد",
  preparationCounts: "شواهد پذیرفته‌شده: {evidence} · کارهای آماده‌سازی: {jobs} · آماده‌سازی ثبت‌نشده: {missing}",
  preparationBody: "آمادگی برای ارجاع ممکن است شامل آماده‌سازی ناقص باشد. پیش از تلقی ابزار خالی به‌عنوان تحلیل تکمیل‌شده، نتیجهٔ پارسر را بررسی کنید.",
  parserStage: "پارسر", correlationStage: "هم‌بستگی", enrichmentStage: "غنی‌سازی", preparationError: "کد خطای آماده‌سازی",
  stage_pending: "در انتظار", stage_queued: "در صف", stage_running: "در حال اجرا", stage_succeeded: "موفق", stage_partial: "ناقص", stage_unavailable: "در دسترس نیست", stage_skipped: "اجرا نشده", stage_failed: "ناموفق", stage_unknown: "نامشخص",
  sourceCounts: "منابع نرمال‌شده: {total} · مشاهدات و رویدادها: {records} · موجودیت‌ها: {entities} · روابط کامل: {relationships}",
  noNormalizedSources: "سرور هیچ منبع نرمال‌شده‌ای برای این کیس برنگردانده است. اجراهای پارسر و پوشش شواهد را بررسی کنید؛ وجود اسنپ‌شات تحلیل به‌تنهایی به معنی آماده بودن دادهٔ قابل تحلیل نیست.",
  noModeSources: "در منابع دریافتی رکوردی از این نوع وجود ندارد. شواهد و اجراهای پارسر را برای محدودیت‌های پوشش و آماده‌سازی بررسی کنید.",
  noMatchingSources: "رکوردی با این فیلترها پیدا نشد. جست‌وجو یا فیلتر نوع داده را پاک کنید تا رکوردهای دیگر نمایش داده شوند.",
  inspectPreparation: "بررسی اجراهای پارسر و پوشش شواهد",
  objects: "داده‌ها",
  relationships: "روابط",
  source: "بررسی منبع",
  close: "بستن بررسی منبع",
  noData: "دادهٔ تحلیلی منطبقی موجود نیست",
  noDataBody:
    "داده‌ای در این نمای مجاز موجود نیست. این به معنی نبود رفتار مشکوک نیست.",
  unresolved:
    "نقاط پایانی {count} رابطه در دسترس نیست و در گراف نمایش داده نمی‌شوند.",
  graphEmpty:
    "رابطهٔ کامل میان موجودیت‌ها موجود نیست. خط زمانی یا شواهد را بررسی کنید.",
  related: "داده‌های مرتبط",
  time: "زمان مشاهده",
  unknownTime: "زمان در دسترس نیست",
  state: "وضعیت شواهد",
  reference: "ارجاع دقیق داده",
  provenance: "اتصالات منبع",
  snapshot: "اسنپ‌شات تحلیل",
  details: "ویژگی‌های ثبت‌شده",
  noProvenance: "اتصال به منبع در دسترس نیست.",
  useHypothesis: "استفاده در فرضیه",
  sources: "منابع انتخاب‌شده",
  clearSources: "پاک کردن منابع انتخاب‌شده",
  detections: "سرنخ‌های خودکار",
  intel: "اطلاعات تهدید",
  hypotheses: "فرضیه‌ها",
  ai: "دستیار هوش مصنوعی",
  future: "قابلیت آینده",
  aiBody:
    "دستیار هوش مصنوعی در این محیط فعال نیست. دستیار آینده باید محدود به کیس، مستند به منابع انتخاب‌شده و نیازمند بررسی انسان باشد؛ نمی‌تواند یافتهٔ تأییدشده ایجاد کند یا گزارش منتشر کند.",
  history: "تاریخچهٔ موضوع",
  cross: "کیس‌های مجاز",
  historyBody:
    "نتیجه‌گیری تاریخی به هویت تأییدشدهٔ موضوع، بازه‌های زمانی قابل مقایسه، محدودهٔ جمع‌آوری و نسخهٔ پارسرها نیاز دارد. نمای فعلی مقایسهٔ تاریخی اعتبارسنجی‌شده ارائه نمی‌دهد؛ تکرار یا نبود رفتار استنباط نمی‌شود.",
  snapshots: "اسنپ‌شات‌های جمع‌آوری ثبت‌شده",
  comparisonBoundary:
    "تطبیق دقیق به معنی احتمال نفوذ یا انتساب به کارزار نیست. دسترسی به شواهد خام محدود به کیس باقی می‌ماند.",
  grant: "مجوز دسترسی تحلیلگر ارشد",
  entries: "شناسه‌های صریح داده‌های مشتق‌شده",
  compare: "مقایسهٔ سیگنال‌های انتخاب‌شده",
  notCompared: "هنوز مقایسه‌ای اجرا نشده است.",
  match: "تطبیق دقیق سیگنال مشتق‌شده",
  noMatch: "سیگنال‌های انتخاب‌شده منطبق نبودند",
  comparisonError:
    "مقایسه در دسترس نیست یا مجوز تغییر کرده است. نتایج قبلی پاک شدند.",
  hypothesisStatement: "متن فرضیه",
  rationale: "دلیل میزان اطمینان",
  confidence: "اطمینان تحلیلگر",
  save: "ذخیرهٔ فرضیه",
  saved: "فرضیه ذخیره شد",
  pending: "در حال ذخیره…",
  failed:
    "عملیات انجام نشد. صفحه را تازه‌سازی و دسترسی خود را پیش از تلاش مجدد بررسی کنید.",
  loading: "در حال دریافت…",
  refresh: "تازه‌سازی",
  reason: "دلیل ارتباط شواهد",
  stance: "موضع شواهد",
  supports: "پشتیبان",
  contradicts: "متناقض",
  limits: "محدودکننده",
  attach: "اتصال شواهد",
  selectHypothesis: "انتخاب فرضیه",
  none: "فرضیه‌ای برای این کیس موجود نیست.",
  intelBody:
    "اطلاعات تهدید و اسنپ‌شات‌های غنی‌سازی زمینهٔ بررسی هستند، نه حکم انسانی. نبود تطبیق امنیت را اثبات نمی‌کند. زمان انتشار، دریافت، اعتبار و زنجیرهٔ منبع را بررسی کنید.",
  rule: "قاعده و نسخه",
  uncertainty: "قطعیت زمانی",
  currentCase: "کیس فعلی",
  graphLimit:
    "نمایش {shown} از {total} دادهٔ گراف. برای محدود کردن بوم فیلتر کنید؛ فهرست داده‌ها قابل جست‌وجو است.",
  selectRecord: "برای بررسی منابع ثبت‌شده یک داده یا رابطه را انتخاب کنید.",
  page: "محیط بررسی کیس",
  role: "تحلیلگر",
  profile: "پروفایل و حساب",
  security: "امنیت و نشست‌ها",
  signout: "خروج",
  help: "راهنما",
  helpBody:
    "یک کیس تخصیص‌یافته را باز کنید، منابع را بررسی کنید، فرضیه ثبت کنید و گزارش را برای بررسی تحلیلگر ارشد ارسال کنید. سرنخ خودکار به معنی یافته نیست.",
  notifications: "اعلان‌ها",
  account: "منوی حساب",
  markRead: "خوانده شد",
  markAll: "خواندن همه",
  noNotifications: "اعلانی وجود ندارد",
  searchCases: "جست‌وجوی کیس‌های تخصیص‌یافته",
  noResults: "کیس تخصیص‌یافتهٔ منطبقی پیدا نشد",
  openSource: "بررسی منبع",
  sourceUnavailable: "منبع در این نمای مجاز موجود نیست.",
  created: "زمان ثبت",
  ends: "پایان بازه",
  precision: "دقت زمان",
  version: "نسخه",
  purpose: "هدف",
  expires: "انقضا",
  notFound: "در دسترس نیست",
  attachSaved: "شواهد متصل شد",
  latestOnly:
    "آخرین فرضیه‌های فعال نمایش داده می‌شوند. نسخه‌های قبلی در تاریخچهٔ ممیزی باقی می‌مانند.",
};
export function useCaseWorkbenchCopy() {
  const { locale } = useWorkspaceCopy();
  return {
    locale,
    t: useCallback(
      (key: WorkbenchKey, values?: MessageValues) =>
        formatMessage(
          (locale === "fa" ? workbenchFa : workbenchEn)[key],
          values,
        ),
      [locale],
    ),
  };
}

export function workbenchEnumKey(value: string): WorkbenchKey | null {
  return Object.hasOwn(workbenchEn, value) ? (value as WorkbenchKey) : null;
}
